import React, { useEffect, useState } from 'react';

import { DateTime } from 'luxon';

import { Box, COLORS, Text } from '@clutter/clean';

import { MovingCheckoutStepProps } from '@root/components/checkout/types';
import { EventSchema } from '@root/resources/wt/types';

import { Calendar } from '@shared/calendar';
import {
  FROM_ISO_OPTIONS,
  hashDateTime,
} from '@shared/calendar/calendar_utils';

import { useStabilizedFunction } from '@utils/hooks';
import { useLongDistanceDeliveryAvailabilities } from '@utils/hooks/availabilities';
import { useAvailabilitySummary } from '@utils/hooks/availability_summary';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { isOutsideNextDayCutoff } from '@utils/time';

export const DeliveryAppointment: React.FC<MovingCheckoutStepProps> = ({
  enabled,
  onChange,
  values: {
    datePreferred,
    scheduleDeliverySelected,
    movingQuote,
    zip,
    unpackingScheduled,
    endAddress,
  },
}) => {
  const trackFunnelEvent = useTrackFunnelEvents();

  const [availableDays, setAvailableDays] = useState<Set<string> | undefined>(
    undefined,
  );

  const currentDay = DateTime.local().set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  const destinationZip = endAddress?.zip ?? '';

  const { from, till, availabilities, fetchMore, loading, fetched } =
    useLongDistanceDeliveryAvailabilities({
      zip: destinationZip,
      movingQuote,
      pickupDate: datePreferred,
      startDate: datePreferred?.plus({ weeks: 1 }),
      skip: !destinationZip || !datePreferred || !movingQuote?.signedID,
    });

  const isUnavailable = useStabilizedFunction(
    (value: DateTime, availableDays: Set<string> | undefined) =>
      !availableDays?.has(hashDateTime(value)) ||
      !isOutsideNextDayCutoff(value),
  );

  // Build availability summary for delivery appointment calendar to power funnel metrics
  const availabilitySummary = useAvailabilitySummary({
    from,
    till,
    currentDay,
    availableDays,
    isUnavailable,
  });

  const totalDays = availabilitySummary.length;
  const unavailableDays =
    totalDays - availabilitySummary.filter(Boolean).length;

  // Fire calendar viewed event when delivery appointment calendar is shown
  useEffect(() => {
    if (!enabled || !fetched) return;
    const visible = movingQuote?.longDistance && scheduleDeliverySelected;
    if (!visible) return;
    const fromISO = from.toISODate();
    if (!fromISO) return;
    trackFunnelEvent({
      schema: EventSchema.WWW__DeliveryAppointmentDateViewed,
      action: 'display',
      metadata: {
        from: fromISO,
        originZip: zip ?? '',
        destinationZip: destinationZip,
        totalDays,
        unavailableDays,
      },
    });
  }, [
    enabled,
    fetched,
    movingQuote?.longDistance,
    scheduleDeliverySelected,
    from,
    zip,
    destinationZip,
    totalDays,
    unavailableDays,
    trackFunnelEvent,
  ]);

  useEffect(() => {
    if (loading) {
      return;
    }
    const selectables = new Set<string>();
    for (const availability of availabilities) {
      const selectable = DateTime.fromISO(
        availability.fromTime,
        FROM_ISO_OPTIONS,
      );
      selectables.add(hashDateTime(selectable));
    }
    setAvailableDays(selectables);
  }, [loading, availabilities]);

  const selectDate = (date: DateTime) => {
    if (availableDays?.has(hashDateTime(date))) {
      trackFunnelEvent({
        schema: EventSchema.WWW__DeliveryAppointmentDateCompleted,
        action: 'click',
        metadata: { date: date.toISO() },
      });
      onChange('unpackingScheduled', date);
    }
  };

  const visible = movingQuote?.longDistance && scheduleDeliverySelected;

  if (!visible) {
    return null;
  }

  return (
    <>
      <Box margin={['48px 0 0', '72px 0 0']}>
        <Box.Grid
          gridTemplateAreas={[
            "'title' 'description' 'link'",
            "'title link' 'description description'",
          ]}
          margin="0 0 32px"
        >
          <Box.GridItem gridArea="title">
            <Text.Title size="small" color={COLORS.tealDark}>
              When do you want to schedule your delivery?
            </Text.Title>
          </Box.GridItem>
          <Box.GridItem margin="4px 0 0" gridArea="description">
            <Text.Body color={COLORS.storm}>
              You can reschedule for free up to 48 hours before your
              appointment.
            </Text.Body>
          </Box.GridItem>
        </Box.Grid>
        <Calendar
          from={from}
          till={till}
          fetchMore={loading ? undefined : fetchMore}
          selected={(value) =>
            !!unpackingScheduled &&
            unpackingScheduled.toISODate() === value.toISODate()
          }
          pastDay={(value) => value <= currentDay}
          unavailable={(value) => isUnavailable(value, availableDays)}
          onSelect={(value) => selectDate(value)}
        />
      </Box>
    </>
  );
};
